import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import {
  CheckCircle,
  Info,
  Warning as AlertTriangle,
  X,
  XCircle
} from '@phosphor-icons/react';

const ToastContext = createContext(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const Toast = ({ toast, onRemove }) => {
  const icons = {
    success: <CheckCircle className="h-5 w-5 text-emerald-400" />,
    error: <XCircle className="h-5 w-5 text-red-400" />,
    warning: <AlertTriangle className="h-5 w-5 text-amber-400" />,
    info: <Info className="h-5 w-5 text-cyan-400" />,
  };

  const backgrounds = {
    success: 'bg-emerald-500/10 border-emerald-500/30',
    error: 'bg-red-500/10 border-red-500/30',
    warning: 'bg-amber-500/10 border-amber-500/30',
    info: 'bg-cyan-500/10 border-cyan-500/30',
  };

  return (
    <div
      className={`
        flex items-start gap-3 p-4 rounded-lg border backdrop-blur-sm
        shadow-lg animate-slide-in-right
        ${backgrounds[toast.type] || backgrounds.info}
      `}
      role="alert"
      // Selector estable para los E2E: al sustituir los alert() del navegador
      // por avisos propios, las pruebas dejaron de poder leer el mensaje
      // (capturaban `page.on('dialog')`). Ver design_review/e2e/_harness.js.
      data-testid="toast"
      data-toast-type={toast.type || 'info'}
    >
      <div className="flex-shrink-0 mt-0.5">
        {icons[toast.type] || icons.info}
      </div>
      <div className="flex-1 min-w-0">
        {toast.title && (
          <p className="text-sm font-semibold text-v4-txt">{toast.title}</p>
        )}
        {/* `whitespace-pre-line`: varios de los avisos que antes eran `alert()`
            traen diagnósticos de varias líneas ("Verifica: - agente activo…").
            Un `alert()` del navegador respetaba los saltos y un <p> normal los
            colapsaba, así que al migrarlos (FE-10) el texto se habría quedado
            en un párrafo ilegible. `break-words` evita que una URL o un
            identificador largo se salga de la tarjeta. */}
        <p className={`text-sm text-slate-300 whitespace-pre-line break-words ${toast.title ? 'mt-1' : ''}`}>
          {toast.message}
        </p>
      </div>
      <button
        onClick={() => onRemove(toast.id)}
        className="flex-shrink-0 p-1 text-slate-400 hover:text-v4-txt rounded transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  // Un `alert()` no se iba hasta que el usuario lo cerraba. Al migrarlos
  // (FE-10), un aviso de error largo con 5 s fijos no se alcanza a leer: se da
  // más tiempo a los mensajes largos y a los errores. `duration: 0` sigue
  // significando "no se cierra solo" y un valor explícito sigue mandando.
  const defaultDuration = (type, title, message) => {
    const chars = `${title || ''} ${message || ''}`.length;
    const base = type === 'error' ? 8000 : 5000;
    // ~18 caracteres por segundo de lectura, con techo de 15 s.
    return Math.min(Math.max(base, Math.round((chars / 18) * 1000)), 15000);
  };

  const addToast = useCallback(({ type = 'info', title, message, duration }) => {
    const id = Date.now() + Math.random();
    const ttl = duration === undefined ? defaultDuration(type, title, message) : duration;

    setToasts(prev => [...prev, { id, type, title, message }]);

    if (ttl > 0) {
      setTimeout(() => {
        removeToast(id);
      }, ttl);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Objeto estable: las páginas lo meten en dependencias de useCallback/useEffect
  // (cargas con polling); si cambiara en cada aviso, relanzaría esas cargas.
  const toast = useMemo(() => ({
    success: (message, title) => addToast({ type: 'success', message, title }),
    error: (message, title) => addToast({ type: 'error', message, title }),
    warning: (message, title) => addToast({ type: 'warning', message, title }),
    info: (message, title) => addToast({ type: 'info', message, title }),
    custom: addToast,
  }), [addToast]);

  return (
    <ToastContext.Provider value={toast}>
      {children}

      {/* Toast Container */}
      <div className="fixed bottom-4 right-4 z-[200] flex flex-col gap-2 max-w-sm w-full pointer-events-none">
        {toasts.map(t => (
          <div key={t.id} className="pointer-events-auto">
            <Toast toast={t} onRemove={removeToast} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
